// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

// Functional core for the interactive loop highlight (MVP WS2, AC2.2): given a
// feedback loop's member variables + polarity, build the variable-highlight map
// that emphasises those members on the canvas (reusing the existing overlay).
// Pure, so it unit-tests without a live model.

import { HighlightKind, VariableHighlight } from './overlay/highlights';
import { CLDLoopPolarity } from './drawing/cld/CLDLoopLabel';
import { PaperKind, loopColor } from './canvas-notation';

/**
 * Build the variable-highlight map for a loop's members, so hovering or selecting
 * a loop emphasises its member variables on the canvas (AC2.2). Every member
 * glows in the loop's badge color (the locked cyan/slate duo, paper-aware), so
 * the highlight matches the R#/B# badge; keyed by ident (duplicates in the loop's
 * variable list collapse to one highlight).
 */
export function loopHighlights(
  variables: readonly string[],
  polarity: CLDLoopPolarity,
  kind: HighlightKind = 'glow',
  paper: PaperKind = 'light',
): Map<string, VariableHighlight> {
  const color = loopColor(polarity, paper);
  const map = new Map<string, VariableHighlight>();
  for (const ident of variables) {
    map.set(ident, { ident, kind, color });
  }
  return map;
}
